import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export type SiteSettingKey =
  | "subscription_bypass"
  | "auto_approve_promotions"
  | "registration_disabled"
  | "auth_google_enabled"
  | "auth_apple_enabled"
  | "raffle_audit_retention_days"
  // Ad engine — pricing & toggles
  | "ad_feed_promotions_enabled"
  | "ad_banner_ads_enabled"
  | "ad_sponsored_posts_enabled"
  | "ad_directory_ads_enabled"
  | "ad_category_takeover_enabled"
  | "ad_travelhub_ads_enabled"
  | "ad_max_promoted_per_feed"
  | "ad_feed_spotlight_price"
  | "ad_featured_listing_price"
  | "ad_bump_price"
  | "ad_max_banner_per_page"
  | "ad_max_sponsored_per_feed"
  | "ad_max_directory_promoted"
  // Plan / subscription controls
  | "plan_new_subscriptions_disabled"
  | "plan_user_self_cancel_enabled";

export const useSiteSetting = (key: SiteSettingKey) => {
  return useQuery({
    queryKey: ["site_settings", key],
    queryFn: async () => {
      const { data } = await supabase
        .from("site_settings")
        .select("value")
        .eq("key", key)
        .maybeSingle();
      return data?.value === "true";
    },
    staleTime: 30_000,
  });
};

export const updateSiteSetting = async (key: SiteSettingKey, value: boolean) => {
  const { error } = await supabase
    .from("site_settings")
    .update({ value: String(value), updated_at: new Date().toISOString() })
    .eq("key", key);
  return { error };
};

/** Read a numeric/string site setting raw value */
export const useSiteSettingValue = (key: SiteSettingKey) => {
  return useQuery({
    queryKey: ["site_settings_value", key],
    queryFn: async () => {
      const { data } = await supabase
        .from("site_settings")
        .select("value")
        .eq("key", key)
        .maybeSingle();
      return data?.value ?? null;
    },
    staleTime: 30_000,
  });
};

export const setSiteSettingValue = async (key: SiteSettingKey, value: string) => {
  // Upsert so missing keys are created
  const { error } = await supabase
    .from("site_settings")
    .upsert(
      { key, value, updated_at: new Date().toISOString() } as any,
      { onConflict: "key" }
    );
  return { error };
};
